import { canCreateUsers } from '@/auth/roles';
import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputFile } from '@/components/forms/form-input-file';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createCoachUsersByCsv, getCoaches, getCoachesMissingZoomLicense, getUsers } from '@/lib/api-calls';
import { csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils';
import { pluralize } from '@/lib/string-utils';
import { postCoachUserCSVRow, PostCoachUsersCSVFormKeys } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateCoachesByCsvProps {
  trigger: ReactNode;
  from?: 'users' | 'coaches' | 'missing-zoom';
}
const EMPTY_ERRORS: string[] = [];
const formSchema = csvFormSchema;

export const CreateCoachesByCsv = ({ trigger, from }: CreateCoachesByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: createCoachUsersByCsv,
  });
  const { actingUser } = useSession();

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: postCoachUserCSVRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(PostCoachUsersCSVFormKeys.coachesCSV, file);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
    });

    const { successRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      toast({ title: `${successRowsCount} new ${pluralize(successRowsCount, 'teacher', 'teachers')} created` });

      switch (from) {
        case 'coaches':
          queryClient.invalidateQueries(getCoaches());
          break;
        case 'users':
          queryClient.invalidateQueries(getUsers());
          break;
        case 'missing-zoom':
          queryClient.invalidateQueries(getCoachesMissingZoomLicense());
          break;
        default:
          queryClient.invalidateQueries(getCoaches());
          break;
      }
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  if (!canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Create teachers by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormInputFile
                form={form}
                name="csvFile"
                label="Teachers CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1_tACJW-bRp0eUSCixjKD6O6IGYX1hMzN8yFIT_cfGIg/edit?usp=sharing"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template (to update)
                </a>{' '}
                for CSV template. Export the sheet as csv file and edit it. Do NOT change the header row
              </span>
            </div>
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
